import type { ReactNode } from "react";

export function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <label className="insp-field">
            <span className="insp-label">{label}</span>
            {children}
        </label>
    );
}

export function TextField({
    label,
    value,
    onChange,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
}) {
    return (
        <Field label={label}>
            <input
                type="text"
                className="insp-input"
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
            />
        </Field>
    );
}

export function NumberField({
    label,
    value,
    onChange,
    min = 0,
    max = 1200,
    unset,
}: {
    label: string;
    value: number | undefined;
    onChange: (v: number | undefined) => void;
    min?: number;
    max?: number;
    /** Allow clearing back to inherited value. */
    unset?: boolean;
}) {
    return (
        <Field label={label}>
            <input
                type="number"
                className="insp-input"
                value={value ?? ""}
                min={min}
                max={max}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "" && unset) return onChange(undefined);
                    const n = Number(raw);
                    if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
                }}
            />
        </Field>
    );
}

export function ColorField({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string | undefined;
    onChange: (v: string) => void;
}) {
    return (
        <Field label={label}>
            <span className="insp-color">
                <input
                    type="color"
                    value={value && value !== "transparent" ? value.slice(0, 7) : "#ffffff"}
                    onChange={(e) => onChange(e.target.value)}
                />
                <input
                    type="text"
                    className="insp-input"
                    value={value ?? ""}
                    placeholder="inherit"
                    onChange={(e) => onChange(e.target.value)}
                />
            </span>
        </Field>
    );
}

export function SelectField<T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T | undefined;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) {
    return (
        <Field label={label}>
            <select
                className="insp-input"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value as T)}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </Field>
    );
}

export function AlignField({
    label,
    value,
    onChange,
    allowJustify,
}: {
    label: string;
    value: string | undefined;
    onChange: (v: "left" | "center" | "right" | "justify") => void;
    allowJustify?: boolean;
}) {
    const opts = ["left", "center", "right", ...(allowJustify ? ["justify"] : [])] as const;
    return (
        <Field label={label}>
            <span className="insp-align" role="radiogroup" aria-label={label}>
                {opts.map((o) => (
                    <button
                        key={o}
                        type="button"
                        role="radio"
                        aria-checked={value === o}
                        className={`insp-align-btn${value === o ? " active" : ""}`}
                        onClick={() => onChange(o as "left")}
                    >
                        {o === "left" ? "⇤" : o === "center" ? "↔" : o === "right" ? "⇥" : "⇹"}
                    </button>
                ))}
            </span>
        </Field>
    );
}

export function PaddingFields({
    values,
    onChange,
}: {
    values: {
        paddingTop?: number;
        paddingRight?: number;
        paddingBottom?: number;
        paddingLeft?: number;
    };
    onChange: (
        key: "paddingTop" | "paddingRight" | "paddingBottom" | "paddingLeft",
        v: number | undefined,
    ) => void;
}) {
    return (
        <div className="insp-padding">
            <span className="insp-label">Padding</span>
            <div className="insp-padding-grid">
                {(
                    [
                        ["paddingTop", "T"],
                        ["paddingRight", "R"],
                        ["paddingBottom", "B"],
                        ["paddingLeft", "L"],
                    ] as const
                ).map(([key, short]) => (
                    <input
                        key={key}
                        type="number"
                        className="insp-input small"
                        aria-label={`Padding ${short}`}
                        title={key}
                        min={0}
                        max={200}
                        value={values[key] ?? ""}
                        placeholder={short}
                        onChange={(e) => {
                            const raw = e.target.value;
                            onChange(key, raw === "" ? undefined : Math.max(0, Math.min(200, Number(raw))));
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
